import React, { useState, useEffect } from "react";
import "./IBCommissionRequests.css";

const IBCommissionRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [buttonLoading, setButtonLoading] = useState({}); // Track individual button loading states
  const [adminRemark, setAdminRemark] = useState({});

  // Fetch pending withdrawal requests
  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await fetch("https://server.majestyfx.com/admin_ib_withdraw.php");
      const data = await response.json();
      
      if (data.success) {
        setRequests(data.requests);
      } else {
        setError(data.message || "Failed to fetch requests");
      }
    } catch (err) {
      setError("Error fetching requests: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle approve action
  const handleApprove = async (requestId) => {
    const remark = adminRemark[requestId] || "";
    
    if (!remark.trim()) {
      alert("Please enter an admin remark before proceeding.");
      return;
    }

    try {
      setButtonLoading(prev => ({ ...prev, [`${requestId}_approve`]: true }));
      
      const response = await fetch("https://server.majestyfx.com/admin_ib_withdraw.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          request_id: requestId,
          action: "approve",
          admin_remark: remark,
        }),
      });

      const data = await response.json();
      
      if (data.success || response.ok) {
        alert("Request approved successfully!");
        // Remove the processed request from the list
        setRequests(prev => prev.filter(req => req.id !== requestId));
        // Clear the remark
        setAdminRemark(prev => ({ ...prev, [requestId]: "" }));
      } else {
        alert(data.message || "Failed to approve request");
      }
    } catch (err) {
      alert("Error approving request: " + err.message);
    } finally {
      setButtonLoading(prev => ({ ...prev, [`${requestId}_approve`]: false }));
    }
  };

  // Handle reject action
  const handleReject = async (requestId) => {
    const remark = adminRemark[requestId] || "";
    
    if (!remark.trim()) {
      alert("Please enter an admin remark before proceeding.");
      return;
    }

    try {
      setButtonLoading(prev => ({ ...prev, [`${requestId}_reject`]: true }));
      
      const response = await fetch("https://server.majestyfx.com/admin_ib_withdraw.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          request_id: requestId,
          action: "reject",
          admin_remark: remark,
        }),
      });

      const data = await response.json();
      
      if (data.success || response.ok) {
        alert("Request rejected successfully!");
        // Remove the processed request from the list
        setRequests(prev => prev.filter(req => req.id !== requestId));
        // Clear the remark
        setAdminRemark(prev => ({ ...prev, [requestId]: "" }));
      } else {
        alert(data.message || "Failed to reject request");
      }
    } catch (err) {
      alert("Error rejecting request: " + err.message);
    } finally {
      setButtonLoading(prev => ({ ...prev, [`${requestId}_reject`]: false }));
    }
  };

  // Handle remark input change
  const handleRemarkChange = (requestId, value) => {
    setAdminRemark(prev => ({ ...prev, [requestId]: value }));
  };

  // Handle copy wallet address to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Wallet address copied to clipboard!");
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        alert("Wallet address copied to clipboard!");
      } catch (err) {
        alert("Failed to copy wallet address");
      }
      document.body.removeChild(textArea);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  if (loading) {
    return (
      <div className="ib-commission-requests">
        <div className="loading">Loading withdrawal requests...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ib-commission-requests">
        <div className="error">
          <p>{error}</p>
          <button onClick={fetchRequests} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ib-commission-requests">
      <div className="page-header">
        <h1>IB Commission Withdrawal Requests</h1>
        <button onClick={fetchRequests} className="refresh-btn">
          Refresh
        </button>
      </div>

      {requests.length === 0 ? (
        <div className="no-requests">
          <p>No pending withdrawal requests found.</p>
        </div>
      ) : (
        <div className="requests-table-container">
          <table className="requests-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Commission Amount</th>
                <th>Type</th>
                <th>Wallet Address</th>
                <th>Network</th>
                <th>Created At</th>
                <th>Admin Remark</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td>{request.id}</td>
                  <td>{request.email}</td>
                  <td>${parseFloat(request.total_commission).toFixed(2)}</td>
                  <td>{request.type}</td>
                  <td>
                    {request.walletaddress ? (
                      <div className="wallet-address-container">
                        <span className="wallet-address">{request.walletaddress}</span>
                        <button
                          onClick={() => copyToClipboard(request.walletaddress)}
                          className="copy-btn"
                          title="Copy wallet address"
                        >
                          📋
                        </button>
                      </div>
                    ) : (
                      <span className="no-data">N/A</span>
                    )}
                  </td>
                  <td>{request.network || "N/A"}</td>
                  <td>{new Date(request.created_at).toLocaleString()}</td>
                  <td>
                    <textarea
                      value={adminRemark[request.id] || ""}
                      onChange={(e) => handleRemarkChange(request.id, e.target.value)}
                      placeholder="Enter admin remark..."
                      className="remark-input"
                      rows="2"
                    />
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleApprove(request.id)}
                        disabled={buttonLoading[`${request.id}_approve`] || buttonLoading[`${request.id}_reject`]}
                        className={`approve-btn ${buttonLoading[`${request.id}_approve`] ? 'loading' : ''}`}
                      >
                        {buttonLoading[`${request.id}_approve`] ? (
                          <>
                            <span className="spinner"></span>
                            Approving...
                          </>
                        ) : (
                          "Approve"
                        )}
                      </button>
                      <button
                        onClick={() => handleReject(request.id)}
                        disabled={buttonLoading[`${request.id}_approve`] || buttonLoading[`${request.id}_reject`]}
                        className={`reject-btn ${buttonLoading[`${request.id}_reject`] ? 'loading' : ''}`}
                      >
                        {buttonLoading[`${request.id}_reject`] ? (
                          <>
                            <span className="spinner"></span>
                            Rejecting...
                          </>
                        ) : (
                          "Reject"
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default IBCommissionRequests;